var _a, _b;
import getBlobDuration from 'get-blob-duration';
import { produce, immerable } from 'immer';
import { fetchLinkDetail } from 'api/model/services/services-api';
import { nanoid } from 'nanoid';
import { assertNever } from 'libs/assert';
const asyncDelay = (miliseconds, result) => new Promise((resolve) => {
    setTimeout(resolve, miliseconds, result);
});
export class MediaOrigin {
    constructor() {
        this.duration = { status: 'loading' };
        this.size = { status: 'loading', size: 0 };
        this.error = null;
    }
    getDurationInCredits() {
        return this.duration.status !== 'credits' ? null : this.duration.credits;
    }
    hasShortDuration() {
        const duration = this.getDurationInCredits();
        return duration === null
            ? false
            : duration <= 240;
    }
    hasMediumDuration() {
        const duration = this.getDurationInCredits();
        return duration === null
            ? false
            : duration > 240 && duration <= 480;
    }
    hasLongDuration() {
        const duration = this.getDurationInCredits();
        return duration === null
            ? false
            : duration > 480;
    }
    hasValidDuration() {
        if (this.hasLongDuration()) {
            return false;
        }
        if (this.duration.status === 'loading' || this.duration.status === 'seconds') {
            return false;
        }
        if (this.duration.status === 'unknown') {
            return true;
        }
        return true;
    }
}
export class MediaFile extends MediaOrigin {
    constructor(file) {
        super();
        this[_a] = true;
        this.resolveDetail = async () => {
            let duration = { status: 'unknown' };
            let size = { status: 'unknown' };
            const rawFile = this.getRawFile();
            if (this.getError() !== null) {
                return produce(this, (draft) => {
                    draft.duration = duration;
                    draft.size = size;
                });
            }
            try {
                const seconds = await Promise.race([
                    getBlobDuration(rawFile),
                    asyncDelay(5000, null),
                ]);
                if (seconds !== null) {
                    duration = { status: 'seconds', seconds };
                    size = { status: 'loaded', size: rawFile.size };
                }
            }
            catch (error) {
                if (error instanceof MediaError) {
                    global.logger.warn('checking duration failed', { message: error.message, strCode: error.code });
                }
                else {
                    global.logger.error('checking duration failed', {}, error);
                }
            }
            return produce(this, (draft) => {
                draft.duration = duration;
                draft.size = size;
            });
        };
        this.file = file;
    }
    getRawFile() {
        assertNever(this.file.originFileObj !== undefined, 'originFileObj should never be undefined');
        return this.file.originFileObj;
    }
    withCreditsDuration(credits) {
        return produce(this, (draft) => {
            draft.duration = { status: 'credits', credits };
        });
    }
    getError() {
        const rawFile = this.getRawFile();
        if (rawFile.size === 0) {
            return 'invalid-format';
        }
        const fileFormat = rawFile.type;
        if (fileFormat === '') {
            const fileName = rawFile.name;
            const fileNameSuffix = fileName.slice(fileName.lastIndexOf('.') + 1);
            return fileNameSuffix === 'flv' ? null : 'invalid-format';
        }
        const fileType = fileFormat.split('/')[0];
        return (fileType === 'video' || fileType === 'audio') ? null : 'invalid-format';
    }
    getName() {
        return this.file.name.substring(0, this.file.name.lastIndexOf('.'));
    }
}
_a = immerable;
export class MediaUrl extends MediaOrigin {
    constructor(url) {
        super();
        this[_b] = true;
        this.resolveDetail = async (connection) => {
            let duration = { status: 'unknown' };
            let size = { status: 'unknown' };
            const result = await fetchLinkDetail(connection, this.url);
            if (result.isSuccess()) {
                const linkDetail = result.get();
                if (linkDetail.duration !== null && linkDetail.duration !== 'NA') {
                    const durationInSeconds = linkDetail.duration.split(':')
                        .map(Number).reduce((accum, currVal) => accum * 60 + currVal);
                    duration = { status: 'seconds', seconds: durationInSeconds };
                    size = { status: 'loaded', size: Number(linkDetail.filesizeApprox) };
                    return produce(this, (draft) => {
                        draft.duration = duration;
                        draft.size = size;
                        draft.name = linkDetail.title;
                    });
                }
                return this.produceWithError('invalid-format', duration, size);
            }
            if (result.err() === 'not-available') {
                return this.produceWithError('not-available', duration, size);
            }
            if (result.err() === 'invalid-format') {
                return this.produceWithError('invalid-format', duration, size);
            }
            if (result.err() === 'invalid-url') {
                return this.produceWithError('invalid-url', duration, size);
            }
            if (result.err() === 'third-party-error') {
                return this.produceWithError('third-party-error', duration, size);
            }
            return this.produceWithError('cannot-process', duration, size);
        };
        this.url = url;
        this.name = this.url;
        this.id = nanoid();
    }
    withCreditsDuration(credits) {
        return produce(this, (draft) => {
            draft.duration = { status: 'credits', credits };
        });
    }
    getError() {
        return this.error;
    }
    getName() {
        return this.name;
    }
    getId() {
        return this.id;
    }
    produceWithError(error, duration, size) {
        return produce(this, (draft) => {
            draft.error = error;
            draft.duration = duration;
            draft.size = size;
        });
    }
}
_b = immerable;
